import { Box, CircularProgress, IconButton, Paper } from "@mui/material";
import {
    DotLegalEmptyState,
    DotLegalLink,
    DotLegalSearchField,
    DotLegalTooltip,
    useIsOnExtraSmallScreen,
    useIsOnLargeScreen,
    useIsOnMediumScreen,
    useIsOnSmallScreen,
} from "@dotlegal/dotlegal-ui-components";
import React, { useRef, useState } from "react";
import { useTranslation } from "../../../localization/useTranslation.ts";
import { usePanes } from "../../panes/usePanes";
import DocumentPane from "../../panes/documentPane/DocumentPane";
import { useGlobalSearchResultList } from "@common/components/search/useGlobalSearchResultList.hooks.ts";
import SearchResultListContent from "../../../common/components/search/SearchResultListContent.tsx";
import DocumentSearchResultList from "../../../common/components/search/DocumentSearchResultList.tsx";
import { isNullOrWhitespace, removeSearchOperatorCharacters } from "@common/stringOperations.ts";
import DotLegalTabs from "../../../common/components/dotLegalTabs/DotLegalTabs.tsx";
import { TabContent } from "@common/components/dotLegalTabs/DotLegalTabs.types.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { PurpleLockIcon } from "@common/icons.tsx";
import { useUrlProvider } from "../../../useUrlProvider.ts";
import SearchEmptyStateSvg from "./search-empty-state.svg?react";
import GlobalSearchIntroSvg from "./global-search-intro.svg?react";
import { Trans } from "react-i18next";
import { PreviewableDocument } from "@common/components/documentPreview/DocumentPreview.types.ts";
import AdvancedDocumentSearchIcon from "../../../common/components/documentPreview/navigate-to-page-icon.svg?react";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider.tsx";

function GlobalSearchField() {
    const isOnExtraSmallScreen = useIsOnExtraSmallScreen();
    const isOnSmallScreen = useIsOnSmallScreen();
    const isOnMediumScreen = useIsOnMediumScreen();
    const isOnLargeScreen = useIsOnLargeScreen();
    const urlProvider = useUrlProvider();

    const { translateString } = useTranslation();
    const { open: openPane } = usePanes();
    const { addonsFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const { getPurchasePlansUrl } = useUrlProvider();

    const [value, setValue] = useState<string | undefined>(undefined);
    const [resultListIsOpen, setResultListIsOpen] = useState<boolean>(false);
    type SearchTab = "entities" | "documents";
    const [selectedTab, setSelectedTab] = useState<number>(0);
    const searchFieldRef = useRef<HTMLInputElement>(null);

    const {
        entitySearchIsLoading,
        documentSearchIsLoading,
        entitySearchSuggestions,
        fetchEntitySearchSuggestions,
        onShowMoreForEntityTypeClick,
        isEntitySpecificSearch,
        setShowMoreForEntityType,
        documentResults,
        entitySearchCount,
        totalDocumentCount,
        hasMoreDocuments,
        loadMoreDocuments,
        minQueryLength,
    } = useGlobalSearchResultList(true);

    const onCloseResultList = () => {
        searchFieldRef.current?.blur();
        setResultListIsOpen(false);
        setShowMoreForEntityType(undefined);
    };

    const navigateToPurchasePage = () => {
        window.location.href = getPurchasePlansUrl();
    };

    // The result list is dismissed with the preview, not after it: the pane takes the stage, so leaving the
    // list open behind it would put it back in front the moment the pane closes.
    const handleOpenPreview = (documents: Array<PreviewableDocument>, initialDocumentId: string, initialPage?: number, initialMatchSnippet?: string) => {
        openPane(DocumentPane, {
            documents,
            initialDocumentId,
            initialPage,
            initialSearchText: removeSearchOperatorCharacters(value),
            initialMatchSnippet,
        });
        onCloseResultList();
    };

    const getTabOrder = (): Array<SearchTab> => {
        let tabs: Array<SearchTab> = [];
        if (!addonsFeatures.research) {
            tabs.push("entities");
        }
        if (!addonsFeatures.searchService || permissions.globalDocumentSearchAccess) {
            tabs.push("documents");
        }
        return tabs;
    };

    const tabOrder = getTabOrder();
    const activeTab: SearchTab | undefined = tabOrder[selectedTab];

    const getTabContent = (): Array<TabContent> => {
        let tabIndex = 0;
        let tabs: Array<TabContent> = [];
        if (!addonsFeatures.research) {
            tabs.push({
                label: (
                    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
                        <span>{translateString("systemData")}</span>
                        {entitySearchIsLoading || entitySearchSuggestions.length === 0 ? (
                            <></>
                        ) : (
                            (entitySearchSuggestions.length > 0 || !isNullOrWhitespace(value)) && <>({entitySearchCount})</>
                        )}
                    </Box>
                ) as any,
                content: <></>,
                index: tabIndex++,
            });
        }

        if (!addonsFeatures.searchService || permissions.globalDocumentSearchAccess) {
            tabs.push({
                label: (
                    <Box
                        onClick={!addonsFeatures.searchService ? () => navigateToPurchasePage() : undefined}
                        sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                    >
                        <span>{translateString("documents")}</span>
                        {totalDocumentCount === undefined ? <></> : (documentResults.length > 0 || !isNullOrWhitespace(value)) && <>({totalDocumentCount})</>}
                    </Box>
                ) as any,
                content: <></>,
                index: tabIndex,
                disabled: !addonsFeatures.searchService,
                icon: !addonsFeatures.searchService ? <Box marginLeft={1}>{PurpleLockIcon}</Box> : undefined,
                tooltip: !addonsFeatures.searchService ? translateString("upgradeToSearchService") : undefined,
            });
        }
        return tabs;
    };

    function getLoadingState() {
        return (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 265 }}>
                <CircularProgress />
            </Box>
        );
    }

    function getDotLegalEmptyState() {
        const showIntro = isNullOrWhitespace(value) || value!.length < minQueryLength;
        return (
            <DotLegalEmptyState
                noFixedHeight
                icon={showIntro ? <GlobalSearchIntroSvg /> : <SearchEmptyStateSvg />}
                title={showIntro ? translateString("globalSearchIntroTitleText") : translateString("globalSearchEmptyTitleText")}
                text={
                    showIntro ? (
                        <Trans
                            i18nKey="globalSearchIntroText"
                            components={{
                                here: (
                                    <DotLegalLink underline to={"https://www.dotlegal.com/product-updates/release-document-search-ocr-november-2025"}>
                                        here
                                    </DotLegalLink>
                                ),
                            }}
                        />
                    ) : (
                        translateString("globalSystemDataSearchEmptyText")
                    )
                }
            />
        );
    }

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: isOnExtraSmallScreen ? 200 : isOnSmallScreen ? 250 : isOnMediumScreen ? 300 : isOnLargeScreen ? 390 : 500,
                minWidth: 200,
            }}
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    onCloseResultList();
                }
            }}
        >
            <DotLegalSearchField
                useFullWidth
                value={value ?? ""}
                onChange={(e: string) => {
                    setValue(e);
                    fetchEntitySearchSuggestions(e);
                }}
                labelSearch={translateString("search")}
                borderRadius={1}
                onFocus={() => {
                    setResultListIsOpen(true);
                }}
                onBlur={() => onCloseResultList()}
                inputRef={searchFieldRef}
            />

            {resultListIsOpen && (
                <Box
                    sx={{
                        position: "absolute",
                        top: "100%",
                        left: 0,
                        marginTop: 1,
                        marginLeft: "2px",
                    }}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <Paper
                        elevation={2}
                        sx={(theme) => ({
                            paddingX: 2,
                            paddingTop: 0,
                            paddingBottom: 2,
                            width: 1100,
                            "& .MuiButtonBase-root.MuiMenuItem-root.Mui-selected": {
                                backgroundColor: theme.palette.primary.light,
                            },
                            fontSize: theme.typography.pxToRem(14),
                            zIndex: 2000,
                        })}
                    >
                        <Box sx={{ mr: -2, ml: -2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <DotLegalTabs
                                tabStyle={"secondary"}
                                selectedTab={selectedTab}
                                setSelectedTab={setSelectedTab}
                                content={getTabContent()}
                                fullWidth={false}
                                ignoreTextTransformation
                                noPadding
                                divider
                            />
                            {addonsFeatures.searchService && permissions.globalDocumentSearchAccess && (
                                <DotLegalTooltip text={translateString("advancedDocumentSearch")}>
                                    <Box component={"span"} sx={{ marginRight: 1 }}>
                                        <DotLegalLink to={urlProvider.getAdvancedDocumentSearchUrl()}>
                                            <IconButton onClick={onCloseResultList}>
                                                <AdvancedDocumentSearchIcon />
                                            </IconButton>
                                        </DotLegalLink>
                                    </Box>
                                </DotLegalTooltip>
                            )}
                        </Box>
                        {activeTab === "entities" && (
                            <>
                                {entitySearchIsLoading ? (
                                    getLoadingState()
                                ) : entitySearchSuggestions.length > 0 ? (
                                    <SearchResultListContent
                                        items={entitySearchSuggestions}
                                        isLoading={entitySearchIsLoading}
                                        onItemSelected={() => onCloseResultList()}
                                        isLink
                                        isEntitySpecificSearch={isEntitySpecificSearch}
                                        onShowMoreClick={(entityType) => onShowMoreForEntityTypeClick(value ?? "", entityType)}
                                    />
                                ) : (
                                    getDotLegalEmptyState()
                                )}
                            </>
                        )}
                        {activeTab === "documents" && (
                            <>
                                {documentSearchIsLoading ? (
                                    getLoadingState()
                                ) : documentResults.length > 0 ? (
                                    <DocumentSearchResultList
                                        items={documentResults}
                                        onItemSelected={() => onCloseResultList()}
                                        onLoadMore={loadMoreDocuments}
                                        hasMoreResults={hasMoreDocuments}
                                        totalCount={totalDocumentCount}
                                        onOpenPreview={handleOpenPreview}
                                    />
                                ) : (
                                    getDotLegalEmptyState()
                                )}
                            </>
                        )}
                    </Paper>
                </Box>
            )}
        </Box>
    );
}

export default GlobalSearchField;
